// OQL pipeline-language results (oxjob #1536 and #1550, API side #1530).
//
// A pipeline query (`get works where ...; then group those works by ...; then
// calculate ...`) answers with groups that carry calculated columns and a summary
// instead of a page of rows:
//
//   meta.measures = [{key, measure, column_id, oql}]   one per calculated column
//   meta.splits   = [{oql, column_id, kind, has_ids}]  one per split, outer first
//   group_by      = [{key, key_display_name, count, <measure keys>, groups?}]
//   summary       = {all: {key: "all", key_display_name, count, <measure keys>},
//                    splits?: [{groups: [...], more_groups}]}   (2+ splits)
//
// The JSON nests (splits under `groups`); the website's table is flat (#1550,
// Jason: "Flat is what users are used to ... It's what a table should always be"):
// one row per innermost group, a column per split, every row sortable on its own.
// The summary (the whole set, then each split's groups on their own, computed by the
// API from the works) is not shown on the page yet, only downloaded (Jason,
// 2026-10-08). This module is plain JS so it can be unit-tested (no component mounts
// here).

import * as openalexId from "@/openalexId";
import { entityConfigs } from "@/entityConfigs";

// A response is a pipeline response when it names its calculated columns.
export function isPipelineResponse(resultsObject) {
  return Array.isArray(resultsObject?.meta?.measures);
}

// An OQO is a pipeline query when it calculates, or splits in a way today's
// group-by can't (listed values, bins, conditions, group filters). Its builder
// render (`oql_render_v2`) is null, so the builder can't show it.
export function isPipelineOqo(oqo) {
  if (!oqo || typeof oqo !== "object") return false;
  if (Array.isArray(oqo.calculate) && oqo.calculate.length) return true;
  return (oqo.group_by || []).some(
    (g) => g && typeof g === "object" && Object.keys(g).some((k) => k !== "column_id"),
  );
}

// The works a calculation is computed from (its "starting set"): the query with its
// calculations, splits and order taken off. Feeds the NL page's Works tab and its chart views
// of a works list (oxjob #1745); every other key (filters, corpus, sample) stays.
// A split over listed values (`by institution in ([UF], [FSU], [USF])`) also limits the works:
// only those in one of its groups are counted, so the starting set keeps it as an OR filter
// (#1744: without it the Works tab listed all 144M articles behind a three-university table).
export function startingSetOqo(oqo) {
  const { calculate, group_by, sort_by, ...rest } = oqo || {}; // eslint-disable-line no-unused-vars
  const limits = (group_by || [])
    .filter((g) => Array.isArray(g?.values) && g.values.length)
    .map((g) => ({
      join: "or",
      filters: g.values.map((v) => (v && typeof v === "object" ? v : { column_id: g.column_id, value: v })),
    }));
  if (!limits.length) return rest;
  return { ...rest, filter_rows: [...(rest.filter_rows || []), ...limits] };
}

// Percent measures print with a % sign; everything else is a plain number.
const PERCENT_MEASURES = new Set(["percent", "percent_of_those"]);

export function formatMeasure(measure, value) {
  if (value == null || Number.isNaN(value)) return "–";
  if (typeof value !== "number") return String(value);
  if (PERCENT_MEASURES.has(measure?.measure)) {
    return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
  }
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

// A group's link: entity groups (institutions, authors, SDGs, countries...) link to
// their entity page; yes/no groups, bins, conditions and searches have no page.
export function groupLink(key) {
  if (typeof key !== "string" || !key.startsWith("https://openalex.org/")) return null;
  const entityType = openalexId.getEntityType(key);
  const shortId = openalexId.getShortId(key);
  if (!entityType || !shortId || !entityConfigs[entityType]) return null;
  return `/${entityType}/${shortId}`;
}

// The flat groups table: one row per innermost group. `path` holds the group at
// each split, outer first (the cells of the split columns); `group` is the
// innermost one (its calculated columns). The id is the path of keys, so it
// survives a re-sort.
export function flatRows(groups, depth, path = []) {
  const rows = [];
  for (const g of Array.isArray(groups) ? groups : []) {
    const p = [...path, g];
    if (p.length < depth) rows.push(...flatRows(g.groups, depth, p));
    else rows.push({ id: p.map((x) => x.key).join("/"), path: p, group: g });
  }
  return rows;
}

// The whole set has no share of a parent and no group's own field (an author's
// h-index): those columns don't apply to its row.
const NO_WHOLE_SET = new Set(["percent_of_those", "value"]);
export function wholeSetMeasures(measures) {
  return (measures || []).filter((m) => !NO_WHOLE_SET.has(m?.measure));
}

// ---- Exports (the shared Export dialog, users-api's exporter; oxjob #1550) --------
// Jason, 2026-10-08: OQL has no limits, so an export writes every group, priced like
// works exports: the query's price for every 100 rows. These mirror users-api's
// export_calculation.py (export_rows / export_credits), so the dialog's price is the
// one the server checks.
export const ROWS_PER_PRICE = 100;

function countLeaves(groups, depth, n) {
  let total = 0;
  for (const g of groups || []) total += depth + 1 < n ? countLeaves(g.groups, depth + 1, n) : 1;
  return total;
}

const splitsOf = (resultsObject) => (resultsObject?.meta?.splits || []).length;

// Group rows an export writes: no split 1; else the API's row count, `rows_count` (a nested
// split's combinations: every one within the limits, an estimate past them, oxjob #1598).
// An answer without it: one split the group count (an estimate past 3,000); nested splits
// all their combinations. null when unknown.
export function exportRows(resultsObject) {
  const meta = resultsObject?.meta || {};
  const n = splitsOf(resultsObject);
  if (n === 0) return 1;
  if ('rows_count' in meta) return meta.rows_count ?? null;
  if (n === 1) return meta.groups_count ?? null;
  return countLeaves(resultsObject?.group_by, 0, n);
}

// The summary's rows: all works plus each split's groups on their own. A split the
// answer lists only the first groups of exports every one of them, so each counts its
// groups_count when the API knows it (oxjob #1598).
export function summaryRows(resultsObject) {
  const parts = resultsObject?.summary?.splits || [];
  return 1 + parts.reduce((s, p) => s + Math.max(p?.groups_count || 0, (p?.groups || []).length), 0);
}

const priced = (price, rows) => price * Math.max(1, Math.ceil(rows / ROWS_PER_PRICE));

// The query's price for every 100 group rows (at least once), plus the same for the
// summary's rows when there is a split (its own call). null when the rows are unknown.
export function exportCredits(resultsObject) {
  const rows = exportRows(resultsObject);
  if (rows == null) return null;
  const price = resultsObject?.meta?.cost?.credits || 1;
  return priced(price, rows) + (splitsOf(resultsObject) ? priced(price, summaryRows(resultsObject)) : 0);
}

// What a calculation exports, nothing to choose (Jason, 2026-10-09): with no split the
// one row as a CSV; with splits one zip of every group and the summary.
export function calculationExportFile(resultsObject) {
  const n = splitsOf(resultsObject);
  if (n === 0) return { label: "CSV", desc: "The calculation for the whole set, one row." };
  return {
    label: "Zip",
    desc: n >= 2
      ? "Every group (groups.csv), plus the summary: all works, and each split on its own."
      : "Every group (groups.csv), plus the summary: all works (all-works.csv).",
  };
}

// A row's value for a sort key: `split:<i>` sorts by that split's group name, any
// other key is one of the row's calculated columns.
function rowValue(row, key) {
  if (key.startsWith("split:")) return row.path?.[Number(key.slice(6))]?.key_display_name;
  return row.group?.[key];
}

// Missing values (a mean over no values) always sort last, whichever the
// direction, so they never crowd the top.
function compareRows(a, b, sort) {
  const va = rowValue(a, sort.key);
  const vb = rowValue(b, sort.key);
  const aMissing = va == null || Number.isNaN(va);
  const bMissing = vb == null || Number.isNaN(vb);
  if (aMissing || bMissing) return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
  const sign = sort.dir === "asc" ? 1 : -1;
  if (typeof va === "string" || typeof vb === "string") {
    return sign * String(va).localeCompare(String(vb), undefined, { numeric: true });
  }
  return sign * (va - vb);
}

// A group's own field that comes back as text (an author's last known institution, an
// institution's country; oxjob #1555) is a text column: left-aligned and wrapping, sorted
// A-Z on the page. Numbers and yes/no own fields stay numeric.
export function isTextMeasure(measure, rows) {
  if (measure?.measure !== "value") return false;
  return (rows || []).some((r) => typeof r?.group?.[measure.key] === "string");
}

// Sort flat rows on any column (stable); null sort keeps the API's order.
export function sortRows(rows, sort) {
  if (!Array.isArray(rows)) return [];
  if (!sort?.key) return rows;
  return rows
    .map((r, i) => [r, i])
    .sort(([a, ia], [b, ib]) => compareRows(a, b, sort) || ia - ib)
    .map(([r]) => r);
}

// The results head (oxjob #1930): "N groups of M works", or the whole set's works (`groups` null: no split). An
// answer estimated from a sample of a too-big set says "about" for its works, and "N+" groups when the API says its
// group count is only those the sample holds (meta.estimate.groups_count_is_at_least).
export function countsLabel({ count, groups, estimate }) {
  const n = count != null ? count.toLocaleString("en-US") : null;
  if (groups == null) return n == null ? "" : `${estimate ? "About " : ""}${n} works`;
  const atLeast = Boolean(estimate?.groups_count_is_at_least);
  const head = `${groups.toLocaleString("en-US")}${atLeast ? "+" : ""} ${groups === 1 && !atLeast ? "group" : "groups"}`;
  return n == null ? head : `${head} of ${estimate ? "about " : ""}${n} works`;
}

// Price line for a /query check or an executed pipeline response's meta.cost.
export function formatCost(cost) {
  if (!cost || cost.credits == null) return null;
  const credits = `${cost.credits.toLocaleString("en-US")} credit${cost.credits === 1 ? "" : "s"}`;
  if (cost.usd == null) return credits;
  const usd = cost.usd < 0.01
    ? `$${cost.usd.toLocaleString("en-US", { maximumSignificantDigits: 2 })}`
    : `$${cost.usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${credits} (${usd})`;
}
